import React from 'react';
import { CheckCircle, AlertCircle, Info, XCircle, X } from 'lucide-react';
import clsx from 'clsx';

interface AlertAction {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary';
}

interface AlertProps {
  type: 'success' | 'error' | 'warning' | 'info';
  title?: string;
  message: string;
  dismissible?: boolean;
  onDismiss?: () => void;
  className?: string;
  actions?: AlertAction[];
}

const Alert: React.FC<AlertProps> = ({
  type,
  title,
  message,
  dismissible = false,
  onDismiss,
  className,
  actions,
}) => {
  const variants = {
    success: {
      container: 'bg-green-50 border border-green-200 text-green-800',
      icon: <CheckCircle className="w-5 h-5 text-green-400" />,
      button: 'text-green-500 hover:text-green-600',
    },
    error: {
      container: 'bg-red-50 border border-red-200 text-red-800',
      icon: <XCircle className="w-5 h-5 text-red-400" />,
      button: 'text-red-500 hover:text-red-600',
    },
    warning: {
      container: 'bg-yellow-50 border border-yellow-200 text-yellow-800',
      icon: <AlertCircle className="w-5 h-5 text-yellow-400" />,
      button: 'text-yellow-500 hover:text-yellow-600',
    },
    info: {
      container: 'bg-blue-50 border border-blue-200 text-blue-800',
      icon: <Info className="w-5 h-5 text-blue-400" />,
      button: 'text-blue-500 hover:text-blue-600',
    },
  };

  const variant = variants[type];

  return (
    <div
      className={clsx('rounded-lg p-4 flex items-start gap-3', variant.container, className)}
      role="alert"
    >
      <div className="flex-shrink-0">{variant.icon}</div>

      <div className="flex-1 min-w-0">
        {title && <h3 className="text-sm font-medium mb-1">{title}</h3>}
        <p className="text-sm">{message}</p>
        {actions && actions.length > 0 && (
          <div className="mt-3 flex gap-2">
            {actions.map((action, index) => (
              <button
                key={index}
                type="button"
                onClick={action.onClick}
                className={clsx(
                  'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                  action.variant === 'primary'
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                )}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {dismissible && onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className={clsx('flex-shrink-0 p-1 rounded-full transition-colors', variant.button)}
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

export default Alert;
